// biome-ignore lint/style/useImportType: keeping value import for compatibility
import * as React from 'react';
import { type FC, Fragment } from 'react';

import { type CrehanaTFunction, useTranslation } from '@crehana/i18n';
import Book from '@crehana/icons/Book';
import Bulb from '@crehana/icons/Bulb';
import EyeOutline from '@crehana/icons/EyeOutline';
import Download from '@crehana/icons/outline/Download';
import Pencil from '@crehana/icons/outline/Pencil';
import Rocket from '@crehana/icons/outline/Rocket';
import Workplace from '@crehana/icons/pictograms/Workplace';
import { CustomNextImage as Image } from '@crehana/next';
import Clamp from '@crehana/ui/dist/Clamp';
import { grayMain, secondaryMain, white } from '@crehana/ui/dist/styles/colors';
import TextTag from '@crehana/ui/dist/Tag/TextTag';
import { date } from '@crehana/utils';
import Link from 'next/link';

// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { GetDownloadableResources_resources_edges_node as DonwoadableType } from '../../pages/Downloadables/graphql/types/GetDownloadableResources';

interface DownloadableCardProps {
  downloadable: DonwoadableType;
}

const icon = {
  herramientas: Pencil,
  ebooks: Book,
  'top-cases': Bulb,
  tutoriales: Rocket,
};
type TPreviewProps = {
  t: CrehanaTFunction;
};
const Preview: FC<React.PropsWithChildren<TPreviewProps>> = ({ t }) => (
  <Fragment>
    <div className="absolute inset-0 items-center justify-center hidden w-full h-full md:flex bg-base-main-80 preview rounded-5">
      <EyeOutline size={24} color={white} />
      <span className="ml-8 text-white font-link">
        {t('DOWNLOADABLE_CARD_DETAIL')}
      </span>
    </div>
  </Fragment>
);

const DownloadableCard: FC<React.PropsWithChildren<DownloadableCardProps>> = ({
  downloadable,
}) => {
  const { t } = useTranslation();
  const today = new Date();
  const publishedDate = new Date(downloadable.publicationDate);
  const diffTime = Math.abs(publishedDate.valueOf() - today.valueOf());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const isNew = diffDays < 7;

  return (
    <div className="w-full overflow-hidden hover:shadow-dropdown rounded-5">
      <div
        className="relative"
        css={`
          .preview {
            z-index: 2;
            opacity: 0;
            &:hover {
              opacity: 1;
              transition: ease-in 0.3s;
            }
          }
        `}
      >
        <Link href={`/business/descargables/${downloadable.slug}`}>
          {downloadable.coverUrl ? (
            <Image
              alt=""
              src={downloadable.coverUrl}
              layout="responsive"
              loader={({ src }) => `${src}?auto=format&q=80`}
              className="rounded-5"
              height={140}
              width={270}
              objectFit="contain"
              loading="lazy"
            />
          ) : (
            <div
              className="flex items-center justify-center w-full bg-gray-light rounded-5"
              style={{ height: 180 }}
            >
              <Workplace size={56} color={grayMain} secondaryColor={grayMain} />
            </div>
          )}
          <Preview t={t} />
        </Link>
        <div
          className="absolute flex items-center z-5"
          style={{ bottom: -12, left: 16 }}
        >
          {isNew && (
            <TextTag label="Nuevo" className="mr-8 capitalize" type="green" />
          )}
          <TextTag
            label={downloadable.category || ''}
            className="capitalize"
            leftIcon={{
              component: icon[downloadable.category as keyof typeof icon],
            }}
          />
        </div>
      </div>
      <div className="px-16 py-24 bg-white">
        <p className="mb-8 font-label text-secondary-main">
          {downloadable.resourceCategory?.name}
        </p>
        <Link href={`/business/descargables/${downloadable.slug}`}>
          <Clamp clamp={2} className="h-40 font-subtitle3 text-base-main">
            {downloadable.title}
          </Clamp>
        </Link>
        <Clamp
          clamp={3}
          className="mt-8 mb-24 font-body4"
          style={{ height: 60 }}
        >
          {downloadable.description}
        </Clamp>
        <div className="flex items-center justify-between">
          <Clamp clamp={1} className="font-legals text-base-lighter">
            {date.format(downloadable.publicationDate, 'DD/MM/YYYY')}
          </Clamp>
          <p className="flex items-center font-legals text-base-lighter">
            {`${downloadable.downloads} ${t('DOWNLOADABLE_DOWNLOADS')}`}
            <Link href={`/business/descargables/${downloadable.slug}`}>
              <Download className="ml-8" size={24} color={secondaryMain} />
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownloadableCard;
